import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  Typography,
  useTheme,
  Zoom
} from '@mui/material';
import { BiCalendarEvent, BiCrosshair, BiErrorAlt } from 'react-icons/bi';

interface IForgotPasswordModal {
  MCQResult: any;
  openForgetPasswordModal: boolean;
  handleClose: () => void;
}

const MCQResultModal = ({
  openForgetPasswordModal,
  MCQResult,
  handleClose
}: IForgotPasswordModal) => {
  const theme = useTheme();
  const result = MCQResult?.data;
  const isPass = result?.division?.toLowerCase() !== 'fail';

  const statusColor = isPass ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Dialog
      open={openForgetPasswordModal}
      onClose={handleClose}
      TransitionComponent={Zoom}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          p: 1
        }
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
        <IconButton onClick={handleClose} size="small">
          <BiCrosshair />
        </IconButton>
      </Box>

      <DialogContent sx={{ textAlign: 'center', pb: 4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            p: 2,
            borderRadius: '50%',
            bgcolor: alpha(statusColor, 0.1),
            color: statusColor,
            mb: 2
          }}
        >
          {isPass ? (
            <BiCalendarEvent  />
          ) : (
            <BiErrorAlt  />
          )}
        </Box>

        <Typography variant="h5" fontWeight="800" gutterBottom>
          {isPass ? 'Congratulations!' : 'Keep Practicing!'}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {isPass 
            ? 'You have successfully completed the quiz with a great score.' 
            : 'Don’t worry! Review the materials and try again to improve your score.'}
        </Typography>

        <Box
          sx={{
            bgcolor: alpha(theme.palette.background.default, 0.8),
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            p: 3,
            mb: 2
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sx={{ borderRight: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                Score
              </Typography>
              <Typography variant="h4" fontWeight="800" color="primary.main">
                {result?.percentage}%
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" fontWeight="bold" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                Division
              </Typography>
              <Typography 
                variant="h6" 
                fontWeight="700" 
                sx={{ 
                    color: statusColor, 
                    textTransform: 'capitalize' 
                }}
              >
                {result?.division || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleClose}
          sx={{
            py: 1.5,
            borderRadius: 3,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.39)}`,
          }}
        >
          {isPass ? 'Continue to Course' : 'Try Again'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MCQResultModal;